import type { APIOrganizationDataGrant } from '@visorpro/client'
import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { useEffect, useMemo } from 'react'
import type { APICreateOrganizationDataGrantRequest } from '@visorpro/client/dist/RestClient/services'
import { observed } from '../util/observed-decorator'
import { visorPRORestClient } from '../util/api'

export class OrganizationDataGrantsStore {
    public grantsById: Record<string, APIOrganizationDataGrant> = {}
    public grantIds: string[] = []
    public isFetched = false
    public isFetching = false
    public isUpdating = false

    constructor() {
        makeAutoObservable(this)
    }

    @observed('isFetching')
    public async load(organizationId: string) {
        try {
            const response =
                await visorPRORestClient.organizationDataGrants.list({
                    provider_organization_id: organizationId,
                    consumer_organization_id: organizationId,
                })

            const models: Record<string, APIOrganizationDataGrant> = {}
            const modelIds: string[] = []

            response.items.forEach((model) => {
                models[model.id] = model
                modelIds.push(model.id)
            })

            runInAction(() => {
                this.grantsById = models
                this.grantIds = modelIds
                this.isFetched = true
            })
        } catch (e) {
            toast.error(`Failed to fetch data grants: ${(e as Error).message}`)
        }
    }

    @observed('isUpdating')
    public async create(body: APICreateOrganizationDataGrantRequest) {
        try {
            const grant =
                await visorPRORestClient.organizationDataGrants.create(body)

            runInAction(() => {
                this.grantsById[grant.id] = grant
                this.grantIds = [...this.grantIds, grant.id]
            })
        } catch (e) {
            toast.error(`Failed to create data grants: ${(e as Error).message}`)
        }
    }

    @observed('isUpdating')
    public async delete(id: string) {
        try {
            const grant =
                await visorPRORestClient.organizationDataGrants.delete(id)

            runInAction(() => {
                delete this.grantsById[grant.id]
                this.grantIds = this.grantIds.filter(
                    (grantId) => grantId !== grant.id,
                )
            })
        } catch (e) {
            toast.error(`Failed to delete data grants: ${(e as Error).message}`)
        }
    }
}

export const useOrganizationDataGrants = (
    store: OrganizationDataGrantsStore,
    organizationId: string | undefined,
) => {
    useEffect(() => {
        if (organizationId && !store.isFetched && !store.isFetching) {
            void store.load(organizationId)
        }
    }, [store, organizationId])

    return useMemo(() => {
        return Object.values(store.grantsById)
    }, [store.grantsById, store.grantIds])
}
